import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  List,
  ListItem,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Wrap,
  WrapItem
} from '@chakra-ui/react';

const defaultPlaceholder = 'Start typing to select skills...';

const SkillMultiSelect = ({
  skills,
  selectedSkills,
  onSelect,
  onRemove,
  isLoading,
  error,
  onRetry,
  placeholder = defaultPlaceholder,
  inputProps = {},
  showSelectedTags = true
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const comboboxRef = useRef(null);

  const selectedIds = useMemo(() => new Set(selectedSkills.map((skill) => skill.value)), [selectedSkills]);

  const filteredSkills = useMemo(() => {
    const loweredQuery = searchQuery.trim().toLowerCase();
    const availableSkills = skills.filter((skill) => !selectedIds.has(skill.skillid));

    if (!loweredQuery) {
      return availableSkills.slice(0, 10);
    }

    return availableSkills
      .filter((skill) => skill.skillname.toLowerCase().includes(loweredQuery))
      .slice(0, 10);
  }, [skills, searchQuery, selectedIds]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsComboboxOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchQuery, filteredSkills.length]);

  const handleSkillSelect = (skill) => {
    if (!skill) return;
    onSelect?.({ value: skill.skillid, label: skill.skillname });
    setSearchQuery('');
    setIsComboboxOpen(false);
  };

  const handleInputKeyDown = (event) => {
    if (!filteredSkills.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsComboboxOpen(true);
      setHighlightedIndex((prev) => (prev + 1) % filteredSkills.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsComboboxOpen(true);
      setHighlightedIndex((prev) => (prev - 1 + filteredSkills.length) % filteredSkills.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (isComboboxOpen && filteredSkills[highlightedIndex]) {
        handleSkillSelect(filteredSkills[highlightedIndex]);
      }
    } else if (event.key === 'Escape') {
      setIsComboboxOpen(false);
    }
  };

  return (
    <Box>
      {showSelectedTags && selectedSkills.length > 0 && (
        <Wrap spacing={2} mb={3}>
          {selectedSkills.map((skill) => (
            <WrapItem key={skill.value}>
              <Tag size="md" colorScheme="blue" borderRadius="full">
                <TagLabel>{skill.label}</TagLabel>
                <TagCloseButton onClick={() => onRemove?.(skill.value)} />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}
      <Box position="relative" ref={comboboxRef}>
        <Input
          role="combobox"
          aria-expanded={isComboboxOpen}
          aria-controls="skills-combobox-listbox"
          aria-activedescendant={
            isComboboxOpen && filteredSkills[highlightedIndex]
              ? `skills-combobox-option-${filteredSkills[highlightedIndex].skillid}`
              : undefined
          }
          placeholder={
            isLoading
              ? 'Loading skills...'
              : error
                ? 'Unable to load skills'
                : placeholder
          }
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            setIsComboboxOpen(true);
          }}
          onFocus={() => setIsComboboxOpen(true)}
          onKeyDown={handleInputKeyDown}
          autoComplete="off"
          isDisabled={isLoading}
          aria-busy={isLoading}
          {...inputProps}
        />
        {isLoading && (
          <Text fontSize="sm" color="gray.500" mt={2}>
            Loading skills...
          </Text>
        )}
        {error && !isLoading && (
          <Flex mt={2} align="center" gap={2}>
            <Text fontSize="sm" color="red.500">
              {error}
            </Text>
            <Button size="xs" variant="outline" onClick={onRetry}>
              Retry
            </Button>
          </Flex>
        )}
        {isComboboxOpen && filteredSkills.length > 0 && (
          <Box
            mt={1}
            borderWidth="1px"
            borderRadius="md"
            boxShadow="sm"
            maxH="200px"
            overflowY="auto"
            bg="white"
            zIndex={1}
            position="absolute"
            width="100%"
          >
            <List id="skills-combobox-listbox" role="listbox">
              {filteredSkills.map((skill, index) => (
                <ListItem
                  key={skill.skillid}
                  id={`skills-combobox-option-${skill.skillid}`}
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={index === highlightedIndex ? 'blue.50' : 'white'}
                  _hover={{ bg: 'blue.100' }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSkillSelect(skill);
                  }}
                >
                  {skill.skillname}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        {isComboboxOpen && filteredSkills.length === 0 && !isLoading && !error && (
          <Box
            mt={1}
            borderWidth="1px"
            borderRadius="md"
            boxShadow="sm"
            bg="white"
            zIndex={1}
            position="absolute"
            width="100%"
            px={3}
            py={2}
          >
            <Text color="gray.500" fontSize="sm">
              No matching skills found
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SkillMultiSelect;
